import { isValidGpax } from 'gpax';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { callAPI, getNewestTicket, s3CacheKey } from 'helpers';
import { useDispatch } from 'react-redux';

// รายชื่อโรงเรียน (code+name) จาก S3 — โหลดครั้งเดียวตอนพิมพ์ค้นหาครั้งแรก แล้ว filter
// ฝั่ง client แทน GET /schools/autocomplete ที่ยิงทุก keystroke (BE cron update_schools
// เป็นคน gen ไฟล์) — ไม่ preload ตอน mount เพราะ SectionEducation อยู่บนหน้า profile เสมอ
// โหลดพลาด → เคลียร์ promise ให้ครั้งหน้าลองใหม่ แล้วปล่อย onOptionsLoad fallback endpoint เดิม
let schoolsPromise = null;
const loadSchools = () => {
  if (!schoolsPromise) {
    schoolsPromise = callAPI({
      fullURL: `https://my-tcas.s3.ap-southeast-1.amazonaws.com/mytcas/schools.json?timestamp=${s3CacheKey()}`,
    }).catch((error) => {
      schoolsPromise = null;
      throw error;
    });
  }
  return schoolsPromise;
};

// logic เดียวกับ /schools/autocomplete ฝั่ง server: includes แบบ case-insensitive จำกัด 20
// + ชื่อตรงเป๊ะปักหัวเสมอ (กันตัวจริงหลุดเพราะโดน slice ทิ้ง)
const filterSchools = (schools, keyword) => {
  const q = keyword ? String(keyword) : '';
  const needle = q.toLowerCase();
  let matched = q
    ? schools.filter((school) => school.name && school.name.toLowerCase().includes(needle)).slice(0, 20)
    : schools.slice(0, 20);
  const exactMatch = schools.find((school) => school.name === q);
  if (exactMatch) {
    matched = _.uniqBy([exactMatch, ...matched], 'code');
  }
  return matched;
};

const useSchoolSelect = (profile = {}, pendingTickets = []) => {
  const dispatch = useDispatch();
  const { valueOf } = getNewestTicket(pendingTickets, 'update_school');
  const schools = profile.schools || [];
  const maxGpaxSchool =
    _.maxBy(schools, (_school) => Math.max(...[_school.gpax5_score, _school.gpax6_score].filter(isValidGpax).map(Number), -1)) || {};

  const [schoolCode, setSchoolCode] = useState(
    valueOf('school_code') || profile.school_code || _.get(maxGpaxSchool, 'school_code')
  );
  const [selectedOption, setSelectedOption] = useState({
    label: valueOf('school_name') || profile.school_name || _.get(maxGpaxSchool, 'school_name'),
    value: valueOf('school_code') || profile.school_code || _.get(maxGpaxSchool, 'school_code'),
  });

  const [loading, setLoading] = useState(false);
  const [currentSchool, setCurrentSchool] = useState({});
  useEffect(() => {
    // G114-1: เปลี่ยนโรงเรียนแล้วล้างข้อมูลเดิม และรับเฉพาะคำตอบตัวเลือกปัจจุบัน
    let active = true;
    setCurrentSchool({});
    const schoolCode = _.get(selectedOption, 'value');
    if (!schoolCode || schoolCode === 'other') {
      setLoading(false);
      return () => { active = false; };
    }
    const getCurrentSchool = async () => {
      try {
        setLoading(true);
        const { data } = await callAPI({
          url: `/schools/${schoolCode}/info`,
        });
        if (!active) return;
        if (data) {
          setCurrentSchool({
            school_amph_id: data.amph_id,
            school_amph_name: data.amph_name,
            school_code: data.code,
            school_name: data.name,
            school_province_id: data.province_id,
            school_province_name: data.province_name,
            school_tmbl_id: data.tmbl_id,
            school_tmbl_name: data.tmbl_name,
            school_zipcode: data.zipcode,
          });
        }
        setLoading(false);
      } catch (error) {
        if (!active) return;
        console.error(new Error(`Error while trying to get current school: ${error.message}`));
        setLoading(false);
      }
    };

    getCurrentSchool();
    return () => { active = false; };
  }, [selectedOption]);

  return {
    schoolCode,
    onSchoolCodeChange: (code) => {
      setSchoolCode(code);
      dispatch({ type: 'UPDATE_EDUCATION', result: _.find(schools, (_school) => _school.school_code === code) });
    },

    selectedSchool:
      _.find(schools, (_school) => _school.school_code === _.get(selectedOption, 'value')) || currentSchool || {},

    loading,
    currentSchool,

    onOptionsLoad: async (keyword, done) => {
      let schools;
      try {
        schools = filterSchools(await loadSchools(), keyword);
      } catch (error) {
        // S3 ล่ม → endpoint เดิมยังเปิดไว้เป็น fallback ช่วงเปลี่ยนผ่าน
        const { data } = await callAPI({
          url: '/schools/autocomplete',
          query: { q: keyword },
        });
        schools = data;
      }
      done(schools.map((_school) => ({ value: _school.code, label: _school.name })));
    },
    selectedOption,
    onSelectedOptionChange: setSelectedOption,
  };
};

export default useSchoolSelect;
